<template>
	<div
    v-if="showApp"
    id="app-content"
    :class="{ 'apply-transparency': getApplyTransparencyToApp, 'mobile-app': getMobileAppState }"
    :data-theme="[getAppTheme]"
    :style="{'height': keyboardIsbeingShowed ? '100%' : `calc(100% - ${navigationBarHeight}px)`}">
		<div
		v-if="getMobileAppState && getIsCordovaApp"
		class="status-bar-background-layer"
		:class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }"/>
		<!-- Mobile Base Structure -->
		<app-mobile-base
		v-if="getMobileAppState && notDesignShowroom"
		:nonAuthenticatedArea="nonAuthenticatedArea"
		:showLoginSpinner="showLoginSpinner"
		@updateLoginLoader="updateLoginLoaderHandler"
		@reRenderSvg="reRenderSvgFlag = !reRenderSvgFlag"
		@applyLoginLoaderExitAnimation="applyLoginLoaderExitAnimation = true"/>
		<!-- Web Base Structure -->
		<app-web-base
		v-else
		:nonAuthenticatedArea="nonAuthenticatedArea"
		:applyLighterBackground="applyLighterBackground"
		:showLoginSpinner="showLoginSpinner"
		@updateLoginLoader="updateLoginLoaderHandler"
		@applyLoginLoaderExitAnimation="applyLoginLoaderExitAnimation = true"/>
		<!-- Layer for Camera Stuff -->
		<div id="layer-for-camera" class="dont-apply-transparency"></div>
		<!-- *********************************************
		*                                                *
		*                 Teleports Layers               *
		*                                                *
		********************************************** -->
		<!-- These layers are all above our base, and have specific z-index, so we will have layers like shrek or onions -->
		<!-- Viewer Teleport, NOTE z-index is 125, mixin style -> viewerOverlayTeleportStyles -->
		<div id="pdf-vuer-overlay-teleport"/>
		<!-- Search Mobile Layer Overlay Teleport, NOTE z-index is 240, mixin style -> searchMobileLayerOverlayTeleportStyles -->
		<div id="search-mobile-layer-overlay-teleport"/>
		<!-- Side Layer Overlay Teleport, NOTE z-index is 250, mixin style -> sideLayer2OverlayTeleportStyles -->
		<div id="side-layer-overlay-teleport"/>
		<!-- Side Layer Overlay Teleport, NOTE z-index is 315, mixin style -> secondSidelayerOverlayTeleportStyles -->
		<div id="second-side-layer-overlay-teleport"/>
		<!-- More Options Mobile Layer, NOTE z-index is 340, mixin style -> moreOptionsMobilelayerOverlayTeleportStyles -->
		<div id="more-options-mobile-layer-overlay-teleport"/>
		<!-- Modals Overlay Teleport, NOTE z-index is 350, mixin style -> modalsOverlayTeleportStyles -->
		<div id="modals-centered-overlay-teleport"/>
		<!-- Modals Overlay Teleport, NOTE z-index is 380, mixin style -> modalsOverlayTeleportStyles -->
		<div id="second-modals-centered-overlay-teleport"/>
		<!-- Side Layer for Expired Session Side Overlay Teleport, NOTE z-index is 390, mixin style -> expiredSessionSidelayerOverlayTeleportStyles -->
		<div id="expired-session-side-layer-overlay-teleport"/>
		<!-- Toasts Overlay Teleport, NOTE z-index is 450, mixin style -> toatsOverlayTeleportStyles -->
		<div id="toasts-overlay-teleport" class="dont-apply-transparency"/>
		<!-- Modals Over Camera Layer Overlay Teleport, NOTE z-index is 450, mixin style -> toatsOverlayTeleportStyles -->
		<div id="modals-centered-over-camera-teleport" class="dont-apply-transparency"/>
		<!-- Spinner Overlay Teleport, NOTE z-index is 500, mixin style -> spinnerOverlayTeleportStyles -->
		<div id="spinner-overlay-teleport"/>
		<!-- Login Loader -->
		<loaders-login
		v-if="showLoginSpinner"
		:applyLoginLoaderExitAnimation="applyLoginLoaderExitAnimation"
		:triggerLoginSimpleExit="triggerLoginSimpleExit"
		:reRenderSvg="reRenderSvgFlag"
		@closeLoginLoader="[showLoginSpinner = false, triggerLoginSimpleExit = false]"/>
        <!-- Rooted Device Warning Modal -->
        <modal-generic
        v-if="showRootedWarningModal"
        negative
        showOnParent
        leftButtonIcon="check-line"
        :title="modalTitle"
        :text="t('rootedWarningModal.text')"
        :leftButtonText="t('rootedWarningModal.leftButtonText')"
        @hideMe="showRootedWarningModal = false"/>
        <!-- Update Warning -->
        <update-warning
        v-if="getNewVersionData"
        :theme="getAppTheme"
        :newVersionData="getNewVersionData"
        @closeAlert="[updateNewVersionData(null), initialAppRequests()]"/>
    </div>
</template>

<script setup>
/* Assets */
import { nonAuthenticatedRoutes, initialRoutes } from '@/router/helpers/validations'
import { navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { APP_VERSION } from '@/assets/constants/core'
import { isMobileValidator, isWebSmallWindow } from '@/assets/scripts/validators'
import { checkIfElementIsVisible } from '@/assets/scripts/htmlManipulators'
import '@/plugins/postlogin_loader.js'

/* Vue */
import { useRoute } from 'vue-router'
import { computed, watch, ref, defineAsyncComponent, onMounted, onUnmounted } from 'vue'
import { getItemFromSessionStorage, removeItemFromSessionStorage } from '@/assets/scripts/windowStorage'

/* Store */
import { storeToRefs } from 'pinia'
import { useProjectBaseStore } from '@/store/projectBase'
import { useSessionStore } from '@/store/session'
import { useMenuStore } from '@/store/menu'
import { usePaymentsServicesStore } from '@/store/payments/paymentsServices'

/* Firebase */
import { initializeApp } from 'firebase/app'
import { getAnalytics } from 'firebase/analytics'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Async Components */
const AppMobileBase = defineAsyncComponent(() => import('@/AppMobileBase.vue'))
const AppWebBase = defineAsyncComponent(() => import('@/AppWebBase.vue'))
const LoadersLogin = defineAsyncComponent(() => import('@/components/generic/loaders/Login'))
const ModalGeneric = defineAsyncComponent(() => import('@/components/generic/modals/centered/Generic'))
const UpdateWarning = defineAsyncComponent(() => import('@/components/features/nonAuthenticated/UpdateWarning'))

const projectBaseStore = useProjectBaseStore()
const { updateIsMobileApp, updateDeviceInUse, updateIsCordovaApp, updateAppThemeClientType, updateAppThemeMode, checkForUpdates, updateNewVersionData, preLoadStoreData } = projectBaseStore
const { menuFetch, submenuFetch } = useMenuStore()
const { getMobileAppState, getDeviceInUse, getIsCordovaApp, getApplyTransparencyToApp, getClientType, getAppTheme, getAppThemeMode, getStatusBarStyle, getNewVersionData } = storeToRefs(projectBaseStore)
const sessionStore = useSessionStore()
const { forceLogout, updateSessionExpireState } = sessionStore
const { getAllCustomerInfo, getToken, getAccountGroups } = storeToRefs(sessionStore)
const { paymentsOperatorsFetch } = usePaymentsServicesStore()
const currentRoute = useRoute()

const nonAuthenticatedArea = computed(() => {
	return nonAuthenticatedRoutes().has(currentRoute.path) || initialRoutes().has(currentRoute.path)
})

const applyLighterBackground = computed(() => {
	return currentRoute.path === '/home'
})

const notDesignShowroom = computed(() => {
	return !currentRoute.path.includes('designShowroom')
})

watch(() => getStatusBarStyle.value, () => {
	if (getMobileAppState.value && window.cordova) {
		getStatusBarStyle.value === 'lightContent' ? window.StatusBar.styleLightContent() : window.StatusBar.styleDefault()
	}
})

watch(() => getAppThemeMode.value, () => {
	if (getMobileAppState.value && getDeviceInUse.value.toLowerCase() === 'ios') {
		getAppThemeMode.value === 'light' ? window.Keyboard.setKeyboardStyle('light') : window.Keyboard.setKeyboardStyle('dark')
	}
})

const keyboardIsbeingShowed = ref(false)
const navigationBarHeight = ref(0)
const showApp = ref(false)
const showLoginSpinner = ref(false)
const reRenderSvgFlag = ref(false)
const triggerLoginSimpleExit = ref(false)
const applyLoginLoaderExitAnimation = ref(false)
const showRootedWarningModal = ref(false)

const updateLoginLoaderHandler = $event => {
	$event ? showLoginSpinner.value = true : triggerLoginSimpleExit.value = true
}

/* Initial requests for App to work */
const initialAppRequests = async () => {
    if (!getToken.value) {
		updateAppThemeMode('system')
        updateSessionExpireState(false)
		showApp.value = true
		if (window.cordova) checkDeviceUpdates()

		return
	}

	const defaultAccountGroup = getAccountGroups.value.find(obj => obj.default)
	if (defaultAccountGroup) updateAppThemeClientType(defaultAccountGroup.type.toLowerCase())

	let mockPayload
	if (import.meta.env.VITE_MOCKS === 'true' && defaultAccountGroup) {
		mockPayload = { payload: defaultAccountGroup.type }
	}

	try {
		await menuFetch(mockPayload)
		await submenuFetch()
        if (window.cordova && window.cordova.platformId === 'android') window.StatusBar.overlaysWebView(true)
		nonBlockingInitialRequests()
	} catch (error) {
		forceLogout()
	}
}

const nonBlockingInitialRequests = () => {
    paymentsOperatorsFetch()
	showApp.value = true
}

/**********************************************************************
*                                                                     *
*                         	  onDeviceReady                           *
*                                                                     *
**********************************************************************/
const isTextInput = node => {
    return ['INPUT', 'TEXTAREA'].indexOf(node.nodeName) !== -1
}

const resizeFunction = () => {
    if (document.documentElement.clientHeight !== initialHeight.value) {
        const heightDiference = document.documentElement.clientHeight - initialHeight.value
        document.documentElement.style.setProperty('--android-top-padding', `${(heightDiference > 39 ? 28 : (heightDiference - 8))}px`)
    }
    window.removeEventListener('resize', resizeFunction)

    if (window.cordova) {
        setTimeout(() => {
            if (window.StatusBar && window.StatusBar.overlaysWebView) {
                window.StatusBar.overlaysWebView(true)
            }
        }, 2000)
    }
}

const initialHeight = ref(document.documentElement.clientHeight)
const touchTime = ref(null)

const onDeviceReady = () => {
    //TODO review with RP for "pinned"   
	window.cordova.plugin.http.setServerTrustMode('default', () => { console.log('success') }, (error) => { console.log('error', error) })
	if (import.meta.env.VITE_ENV !== 'DEV') {
		let customerId = 'Banco-bai' // For PPRD & PRD env
		if (import.meta.env.VITE_ENV === 'QUA') customerId = 'Banco-bai-qa' // For QUA env
	}

	window.cordova.plugin.http.setDataSerializer('json')
	window.screen.orientation.lock('portrait')
	window.cordova.plugin.http.setRequestTimeout(import.meta.env.VITE_ENV === 'DEV' ? 300.0 : 120.0)
	/* Status Bar plugin logic */
	/******************************************************************
	*                         	iOS only logic                     	  *
	******************************************************************/
	if (window.cordova.platformId === 'ios') {
		window.fullMobileHeight = window.innerHeight
        document.addEventListener('touchstart', e => {
            touchTime.value = new Date()
        }, false)

        document.addEventListener('touchend', e => {
            const diff = new Date() - touchTime.value
            if (diff < 150 && !isTextInput(e.target) && isTextInput(document.activeElement)) {
                document.activeElement.blur()
            }
        }, false)

        window.addEventListener('keyboardDidShow', () => {
            if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                checkIfElementIsVisible(document.activeElement, 0)
            }
        })
	/* Android only logic */
	/******************************************************************
	*                         Android only logic                      *
	******************************************************************/
	} else {
        window.addEventListener('resize', resizeFunction)

        let tryCounter = 4
        const interval = setInterval(() => {
            if (tryCounter-- === 0) {
                clearInterval(interval)
                return
            }

            if (window.StatusBar && window.StatusBar.overlaysWebView) {
                // eslint-disable-next-line no-undef
                StatusBar.overlaysWebView(true)
                clearInterval(interval)
            }
        }, 100)

		/*
		* Logic to handle keyboard being shown
		*/
		window.addEventListener('keyboardWillShow', (event) => { keyboardIsbeingShowed.value = true })
		/*
		* Event for when keyboard is about to be closed.
		*/
		window.addEventListener('keyboardWillHide', () => { keyboardIsbeingShowed.value = false })
		/* Getting NavigationBarHeight */
		window.cordova.plugins.SafeArea.getVisibility(
			data => {
				navigationBarHeight.value = data.effectiveNavigationBarHeight / window.devicePixelRatio
			},
			error => { console.log('error SafeArea getVisibility: ' + error) }
		)
	}
	document.addEventListener('backbutton', onBackKeyDown, false)

    // if (import.meta.env.VITE_ENV === 'DEV_MDW') {
    //     const randomBytes = new Uint8Array(294) // emulating a public key
    //     crypto.getRandomValues(randomBytes)
    //     let randomBinaryString = ''
    //     randomBytes.forEach((v) => { randomBinaryString += String.fromCharCode(v) })
    //     // eslint-disable-next-line no-undef
    //     // const nonce = CryptoJS.SHA1(randomBinaryString)
    //     const nonce = crypto.subtle.digest('SHA-1', randomBinaryString)
    //     // eslint-disable-next-line no-undef
    //     integrity.check(btoa(nonce), function (message) {
    //         // success~
    //         alert(message)
    //     }, function (message) {
    //         // error
    //         alert(message + 'error')
    //     })
    // }

    const modalTitle = ref('')
    window.IRoot.isRooted(event => {
        modalTitle.value = window.cordova.platformId === 'ios' ? t('rootedWarningModal.iosTitle') : t('rootedWarningModal.androidTitle')

        showRootedWarningModal.value = event
    }, err => {
        console.log(err)
    })

	const sharedPreferences = window.plugins.SharedPreferences.getInstance()
	sharedPreferences.get('bai-remember-me-entity-info',
		value => {
			preLoadStoreData(value)
			initialAppRequests()
		},
		err => {
			// Error fetching from SharedPreferences, checking localStorage
			console.log('sharePreferences', err)
			preLoadStoreData()
			initialAppRequests()
		}
	)
}

const onBackKeyDown = () => {
	/* We are catching this to prevent default behaviour of mobile */
}

const checkDeviceUpdates = async () => {
    if (window.sessionStorage.getItem('checkedAppVersion') === 'true') return

	const payload = {
		serviceBrand: getClientType.value === 'company' ? 'CORPORATE' : 'RETAIL'
	}
    try {
        const res = await checkForUpdates(payload)
        const versionStatus = res.data.channelApplicationVersionStatus
        /* deprecated -> mandatory update, hasNewVersion -> optional update */
        if (versionStatus.hasNewVersion || versionStatus.deprecated) updateNewVersionData(versionStatus)

        window.sessionStorage.setItem('checkedAppVersion', true)
    } catch (error) {
		console.log(error)
    }
}

if (window.cordova) {
	/* To trigger mobile needed validations/logic */
	document.addEventListener('deviceready', onDeviceReady, false)
	updateIsMobileApp(window.cordova.platformId === 'android' || window.cordova.platformId === 'ios')
	updateDeviceInUse(window.cordova.platformId)
    updateIsCordovaApp(true)

	if (window.cordova.platformId === 'ios') {
		document.querySelector('html').style.height = '100vh'
	}
}

/* In mobile we only want to trigger this after se setup our httpRequests */
if (!window.cordova) {
	preLoadStoreData()
	initialAppRequests()
}

/* Log of current version for testers */
console.log(`App version v4 ${APP_VERSION}`)

/* If redirect link after refresh we will send our user to that place */
if (getItemFromSessionStorage('bai-v4-aux-redirect-link-after-refresh')) {
	navigateByPath(getItemFromSessionStorage('bai-v4-aux-redirect-link-after-refresh'))
	setTimeout(() => {
		removeItemFromSessionStorage('bai-v4-aux-redirect-link-after-refresh')
	})
}

const checkIfMobileBrowserOrSmallWindow = () => {
    if (window.cordova) return

    updateIsMobileApp(isMobileValidator() || isWebSmallWindow())
    updateDeviceInUse(!isMobileValidator() && isWebSmallWindow() ? 'Desktop' : 'android')

	if (currentRoute.path === '/home' || currentRoute.path === '/mobileMenuhome') {
		getMobileAppState.value ? navigateByPath(routeList.HOME_MOBILE_PATH) : navigateByPath(routeList.HOME_PATH)
	}
}

onMounted(() => {
	if (import.meta.env.VITE_FIREBASE && (!window.cordova)) {
        let firebaseConfig
        if (getClientType.value === 'particular') {
            firebaseConfig = {
                apiKey: 'AIzaSyAq3sFFGmxY02DdYHm31meOXHQVjAvwffY',
                authDomain: 'baidirecto-v4.firebaseapp.com',
                projectId: 'baidirecto-v4',
                storageBucket: 'baidirecto-v4.appspot.com',
                messagingSenderId: '650011752637',
                appId: '1:650011752637:web:62843c5dccd04888809fd4',
                measurementId: 'G-GKLFMR2WGW'
            }
        } else {
            firebaseConfig = {
                apiKey: 'AIzaSyAq3sFFGmxY02DdYHm31meOXHQVjAvwffY',
                authDomain: 'baidirecto-v4.firebaseapp.com',
                projectId: 'baidirecto-v4',
                storageBucket: 'baidirecto-v4.appspot.com',
                messagingSenderId: '650011752637',
                appId: '1:650011752637:web:df22683fdf3b3485809fd4',
                measurementId: 'G-CMCJJGV10N'
            }
        }

		// Initialize Firebase
		const app = initializeApp(firebaseConfig)
		const analytics = getAnalytics(app)
	}
    checkIfMobileBrowserOrSmallWindow()
    window.addEventListener('resize', checkIfMobileBrowserOrSmallWindow)
})

onUnmounted(() => {
    window.removeEventListener('resize', checkIfMobileBrowserOrSmallWindow)
})
</script>

<style lang="scss">
@import '@/styles/main.scss';

#app, #app-content {
	width: 100%;
	height: 100%;
	position: relative;

	overflow: hidden;

	.status-bar-background-layer {
		width: 100%;
		position: absolute;
		top: 0;

		z-index: 10;
		background-color: var(--primary);
		opacity: 0;

		&.mobile-status-bar-android {
			height: var(--android-top-padding); /*Android status bar*/
		}

		&.mobile-status-bar-ios {
			height: constant(safe-area-inset-top); /* iOS 11.0 */
			height: env(safe-area-inset-top); /* iOS 11.2 */
		}
	}

	&.apply-transparency {
		background-color: transparent;

		>:not(.dont-apply-transparency) {
			opacity: 0;
			pointer-events: none;
		}
	}

    &.mobile-app {
        -webkit-user-select: none; /* Safari */
        -ms-user-select: none; /* IE 10 and IE 11 */
        user-select: none; /* Standard syntax */
    }
}

#app-content {
    background-color: var(--secondary-light-4);
}
</style>
